import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useShiftRangeMultiSelect } from '@src/hooks/useShiftRangeMultiSelect';
import { isRecentFileMultiSelectSelectableStatus } from '@src/util/FJMultiSelectRangeUtil';
import FJLoading from '../../common/components/FJLoading/FJLoading';
import FJRecentFileCard from '../FJRecentFileCard/FJRecentFileCard';
import FJLocalStore from '../../store/FJLocalStore';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import FJRequirementsUtil from '../../util/FJRequirementsUtil';
import type { ProjectItem } from '../../types/FJProject';
import styles from './FJRecentFiles.module.less';
import FJSelectAll, { type FJSelectAllListItem } from '@src/common/components/FJSelectAll/FJSelectAll';

/** 列表触底预加载：IntersectionObserver 相对视口的扩展边距（四边相同），越大越早触发 load more */
const RECENT_FILES_LOAD_MORE_ROOT_MARGIN = '120px';
/** 相交比例阈值，0 表示与扩展区域任一重叠即触发 */
const RECENT_FILES_LOAD_MORE_THRESHOLD = 0;

/** SPA 会话内仅上报一次：首屏出现「最近文件」区块时打点，离开视图再返回首页不重打 */
let recentFilesSectionViewLoggedOncePerSession = false;

interface FJRecentFilesProps {
    items: ProjectItem[];
    selectedProjectIds: string[];
    /** 任务列表接口 `total`，与首页分页一致（无则埋点退回 `items.length`） */
    listTotalCount?: number | null;
    /** 是否还有更多任务（分页） */
    listHasMore?: boolean;
    listLoadingMore?: boolean;
    /** 滚动触底加载下一页 */
    onLoadMore?: () => void | Promise<void>;
    onTryAgain?: (id: string) => void;
    onRename?: (id: string) => void;
    onDelete?: (id: string) => void;
    /** 点击卡片缩略图区域 */
    onCardClick?: (id: string) => void;
    onContinue?: (id: string) => void;
    onSetProjectSelection: (ids: Array<string | number>) => void;
    onToggleProjectSelection: (id: string) => void;
    onRangeProjectSelection: (ids: string[]) => void;
    onMultiDelete: (ids: Array<string | number>) => void;
    onClearProjectSelection: () => void;
}

export default function FJRecentFiles({
    items,
    selectedProjectIds,
    listTotalCount = null,
    listHasMore = false,
    listLoadingMore = false,
    onLoadMore,
    onTryAgain,
    onRename,
    onDelete,
    onCardClick,
    onContinue,
    onSetProjectSelection,
    onToggleProjectSelection,
    onRangeProjectSelection,
    onMultiDelete,
    onClearProjectSelection,
}: FJRecentFilesProps) {
    const sectionRef = useRef<HTMLElement>(null);

    const handleSetSelection = useCallback(
        (ids: string[]) => onRangeProjectSelection(ids),
        [onRangeProjectSelection],
    );

    const { handleItemSelectClick } = useShiftRangeMultiSelect({
        items,
        selectedIds: selectedProjectIds,
        onSetSelection: handleSetSelection,
        onToggleOne: onToggleProjectSelection,
        isSelectableStatus: isRecentFileMultiSelectSelectableStatus,
    });

    const isRecentFileSelectable = useCallback(
        (item: FJSelectAllListItem) => isRecentFileMultiSelectSelectableStatus(item.status),
        [],
    );

    const onCardToggleSelected = useCallback(
        (id: string, shiftKey?: boolean) => {
            handleItemSelectClick(id, shiftKey);
        },
        [handleItemSelectClick],
    );
    const sentinelRef = useRef<HTMLDivElement>(null);
    const loadBusyRef = useRef(false);

    useLayoutEffect(() => {
        const sectionEl = sectionRef.current;
        if (recentFilesSectionViewLoggedOncePerSession || !sectionEl || items.length === 0) {
            return;
        }
        recentFilesSectionViewLoggedOncePerSession = true;

        const file_count = listTotalCount != null ? listTotalCount : items.length;

        let processing_file_count = 0;
        let completed_file_count = 0;
        let failed_file_count = 0;
        for (const it of items) {
            if (it.status === GENERATE_STATE.PROCESSING || it.status === GENERATE_STATE.PENDING) {
                processing_file_count += 1;
            } else if (it.status === GENERATE_STATE.SUCCEEDED) {
                completed_file_count += 1;
            } else if (it.status === GENERATE_STATE.FAILED) {
                failed_file_count += 1;
            }
        }

        FJRequirementsUtil.eaLog('recent_files_section_view', {
            file_count,
            processing_file_count,
            completed_file_count,
            failed_file_count,
        });
    }, [items, listTotalCount]);

    useEffect(() => {
        if (!listHasMore || !onLoadMore) {
            return;
        }
        const node = sentinelRef.current;
        if (!node) {
            return;
        }
        const observer = new IntersectionObserver(
            entries => {
                const hit = entries.some(e => e.isIntersecting);
                if (!hit || loadBusyRef.current) {
                    return;
                }
                loadBusyRef.current = true;
                void Promise.resolve(onLoadMore()).finally(() => {
                    loadBusyRef.current = false;
                });
            },
            { root: null, rootMargin: RECENT_FILES_LOAD_MORE_ROOT_MARGIN, threshold: RECENT_FILES_LOAD_MORE_THRESHOLD },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [listHasMore, onLoadMore, items.length]);

    return (
        <section ref={sectionRef} className={styles.box}>
            <h2 className={styles.section_title}>{FJLocalStore._('recent-files')}</h2>
            <div className={styles.grid}>
                {items.map(item => (
                    <div key={item.id} className={styles.card_cell} data-recent-file-card-cell>
                        <FJRecentFileCard
                            item={item}
                            isSelected={selectedProjectIds.some(sid => String(sid) === String(item.id))}
                            isMultiSelectMode={selectedProjectIds.length > 0}
                            onToggleSelected={onCardToggleSelected}
                            onTryAgain={onTryAgain}
                            onRename={onRename}
                            onDelete={onDelete}
                            onCardClick={onCardClick}
                            onContinue={onContinue}
                        />
                    </div>
                ))}
            </div>
            {listHasMore ? <div ref={sentinelRef} className={styles.list_sentinel} aria-hidden /> : null}
            {listLoadingMore ? (
                <div className={styles.list_loading_more} role='status' aria-busy='true'>
                    <FJLoading strokeColor='#9FA2AF' style={{ width: 24, height: 24 }} />
                </div>
            ) : null}
            <FJSelectAll
                list={items}
                selectList={selectedProjectIds}
                setSelectList={onSetProjectSelection}
                multiDelete={onMultiDelete}
                clearSelected={onClearProjectSelection}
                isItemSelectable={isRecentFileSelectable}
            />
        </section>
    );
}
